import React, { useState } from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import Block from './Block';
import { Badge } from '../../components/ui/badge';
import { toneOf } from './CausalEffectBlock';
import {
  ChartContainer, ChartLegend, ChartLegendItem, ChartTooltipContent, useContainerWidth,
} from '../../components/ui/chart';
import { Progress } from '../../components/ui/progress';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { cssZoom } from '../../utils/zoom';

const SERIES_COLORS = [
  'var(--chart-cat-1)',
  'var(--chart-cat-2)',
  'var(--chart-cat-3)',
  'var(--chart-cat-4)',
  'var(--chart-cat-5)',
];

const PREFIX_UNITS = new Set(['€', '$', '£', '¥']);

function fmtNum(value) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const n = Number(value);
  const abs = Math.abs(n);
  if (abs === 0) return '0';
  const digits = abs >= 1_000 ? 1 : abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  const scaled = abs >= 1_000_000 ? n / 1_000_000 : abs >= 1_000 ? n / 1_000 : n;
  const suffix = abs >= 1_000_000 ? 'M' : abs >= 1_000 ? 'k' : '';
  return `${scaled.toFixed(digits)}${suffix}`;
}

function fmtSigned(value) {
  const text = fmtNum(value);
  if (text === '—' || Number(value) < 0) return text;
  if (Number(value) > 0) return `+${text}`;
  return text;
}

function fmtUnit(value, unit) {
  const text = fmtNum(value);
  if (text === '—' || !unit) return text;
  if (PREFIX_UNITS.has(unit)) return `${unit}${text}`;
  if (unit === '%') return `${text}%`;
  return `${text} ${unit}`;
}

function fmtShare(value) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const pct = Number(value) * 100;
  const text = Math.abs(pct - Math.round(pct)) < 0.05 ? String(Math.round(pct)) : pct.toFixed(1);
  return `${text}%`;
}

function prettyToken(value) {
  const text = String(value || '').replace(/_/g, ' ').trim();
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function axisCaption(axis) {
  if (!axis) return '';
  const label = axis.label || '';
  const unit = axis.unit || '';
  if (label && unit && !label.toLowerCase().includes(unit.toLowerCase())) return `${label} (${unit})`;
  return label || unit;
}

function seriesName(series, index) {
  if (series?.label) return series.label;
  if (series?.id || series?.treatment) return prettyToken(series.id || series.treatment);
  return `Series ${index + 1}`;
}

function finitePoints(points) {
  return (points || [])
    .filter((p) => p && Number.isFinite(Number(p.x)) && Number.isFinite(Number(p.y)))
    .map((p) => ({
      x: Number(p.x),
      y: Number(p.y),
      lo: Number.isFinite(Number(p.lo)) ? Number(p.lo) : undefined,
      hi: Number.isFinite(Number(p.hi)) ? Number(p.hi) : undefined,
    }))
    .sort((a, b) => a.x - b.x);
}

function supportRange(support) {
  if (!Array.isArray(support) || support.length < 2) return null;
  const a = Number(support[0]);
  const b = Number(support[1]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return a <= b ? [a, b] : [b, a];
}

function inSupport(x, range) {
  if (!range) return true;
  return x >= range[0] && x <= range[1];
}

function pointAt(a, b, x) {
  const t = (x - a.x) / (b.x - a.x);
  const mix = (u, v) => (Number.isFinite(u) && Number.isFinite(v) ? u + (v - u) * t : undefined);
  return { x, y: a.y + (b.y - a.y) * t, lo: mix(a.lo, b.lo), hi: mix(a.hi, b.hi) };
}

// A single SVG path cannot switch dash mid-stroke, so each support crossing
// starts a new subpath that shares the boundary point.
function curveSegments(points, support) {
  const pts = finitePoints(points);
  const range = supportRange(support);
  if (pts.length === 0) return [];
  if (pts.length === 1) return [{ extrapolated: !inSupport(pts[0].x, range), points: pts }];

  const pieces = [];
  const push = (extrapolated, a, b) => {
    const last = pieces[pieces.length - 1];
    if (last && last.extrapolated === extrapolated) {
      const prev = last.points[last.points.length - 1];
      if (prev.x !== a.x || prev.y !== a.y) last.points.push(a);
      last.points.push(b);
    } else {
      pieces.push({ extrapolated, points: [a, b] });
    }
  };

  for (let i = 0; i < pts.length - 1; i += 1) {
    let chain = [pts[i], pts[i + 1]];
    if (range && pts[i + 1].x !== pts[i].x) {
      range.forEach((bound) => {
        const next = [chain[0]];
        for (let j = 0; j < chain.length - 1; j += 1) {
          const a = chain[j];
          const b = chain[j + 1];
          if (a.x < bound && bound < b.x) next.push(pointAt(a, b, bound));
          next.push(b);
        }
        chain = next;
      });
    }
    for (let j = 0; j < chain.length - 1; j += 1) {
      const a = chain[j];
      const b = chain[j + 1];
      push(!inSupport((a.x + b.x) / 2, range), a, b);
    }
  }
  return pieces;
}

function yOnCurve(points, x) {
  const pts = finitePoints(points);
  if (!pts.length || !Number.isFinite(x)) return null;
  if (x <= pts[0].x) return pts[0].y;
  if (x >= pts[pts.length - 1].x) return pts[pts.length - 1].y;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const a = pts[i];
    const b = pts[i + 1];
    if (x >= a.x && x <= b.x) return pointAt(a, b, x).y;
  }
  return null;
}

// Round-number ticks and the domain they span, so axis labels read 0 / 5k /
// 10k rather than 4.2k / 8.4k. Non-negative data keeps its floor at zero.
function niceAxis(values, count = 4) {
  const nums = values.filter((v) => Number.isFinite(v));
  if (!nums.length) return { lo: 0, hi: 1, ticks: [0, 1] };
  let min = Math.min(...nums);
  let max = Math.max(...nums);
  if (min >= 0) min = 0;
  if (min === max) max = min + (Math.abs(min) || 1);
  const raw = (max - min) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((x) => x >= raw) || raw;
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const ticks = [];
  for (let t = lo; t <= hi + step * 1e-6; t += step) ticks.push(Number(t.toFixed(10)));
  return { lo, hi, ticks };
}

function linePath(points, xScale, yScale) {
  return points.map((p, i) => {
    const cmd = i === 0 ? 'M' : 'L';
    return `${cmd}${xScale(p.x).toFixed(1)},${yScale(p.y).toFixed(1)}`;
  }).join(' ');
}

function bandPath(points, xScale, yScale) {
  const band = points.filter((p) => Number.isFinite(p.lo) && Number.isFinite(p.hi));
  if (band.length < 2) return '';
  const top = band.map((p, i) => `${i === 0 ? 'M' : 'L'}${xScale(p.x).toFixed(1)},${yScale(p.hi).toFixed(1)}`);
  const bottom = band.slice().reverse().map((p) => `L${xScale(p.x).toFixed(1)},${yScale(p.lo).toFixed(1)}`);
  return [...top, ...bottom, 'Z'].join(' ');
}

function kindLabel(kind) {
  if (kind === 'current') return 'Current';
  if (kind === 'recommended') return 'Recommended';
  return prettyToken(kind) || 'Marker';
}

function MarkerShape({ kind, cx, cy, color }) {
  if (kind === 'recommended') {
    const d = 6.5;
    return (
      <polygon
        points={`${cx},${cy - d} ${cx + d},${cy} ${cx},${cy + d} ${cx - d},${cy}`}
        fill={color}
        stroke="var(--card)"
        strokeWidth={2}
      />
    );
  }
  if (kind === 'current') {
    return <circle cx={cx} cy={cy} r={5} fill="var(--card)" stroke={color} strokeWidth={2.5} />;
  }
  return <rect x={cx - 4} y={cy - 4} width={8} height={8} fill={color} stroke="var(--card)" strokeWidth={1.5} />;
}

function EffectCurvesChart({ xAxis, yAxis, series, annotations }) {
  const [containerRef, measuredWidth] = useContainerWidth(640);
  const [tip, setTip] = useState(null);
  const width = Math.max(measuredWidth, 280);
  const height = 300;
  const padL = 48;
  const padR = 12;
  const padT = 10;
  const padB = 28;
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;

  const drawn = (series || []).map((item, index) => ({
    item,
    index,
    name: seriesName(item, index),
    color: SERIES_COLORS[index % SERIES_COLORS.length],
    segments: curveSegments(item.points, item.support),
  })).filter((item) => item.segments.length > 0);

  if (!drawn.length) return null;

  const xs = [];
  const ys = [];
  drawn.forEach(({ item, segments }) => {
    segments.forEach((seg) => {
      seg.points.forEach((p) => {
        xs.push(p.x);
        ys.push(p.y);
        if (Number.isFinite(p.lo)) ys.push(p.lo);
        if (Number.isFinite(p.hi)) ys.push(p.hi);
      });
    });
    (item.markers || []).forEach((marker) => {
      const mx = Number(marker.x);
      const my = Number.isFinite(Number(marker.y)) ? Number(marker.y) : yOnCurve(item.points, mx);
      if (Number.isFinite(mx)) xs.push(mx);
      if (Number.isFinite(my)) ys.push(my);
    });
  });

  const xAx = niceAxis(xs);
  const yAx = niceAxis(ys);
  const xScale = (x) => ((x - xAx.lo) / (xAx.hi - xAx.lo)) * plotW;
  const yScale = (y) => plotH - ((y - yAx.lo) / (yAx.hi - yAx.lo)) * plotH;
  const hasExtrapolation = drawn.some(({ segments }) => segments.some((seg) => seg.extrapolated));
  const markerKinds = new Set();
  drawn.forEach(({ item }) => {
    (item.markers || []).forEach((marker) => {
      if (marker.kind) markerKinds.add(marker.kind);
    });
  });
  const xCaption = axisCaption(xAxis);
  const yCaption = axisCaption(yAxis);
  const vlines = (annotations || []).filter((item) => item && item.kind === 'vline' && Number.isFinite(Number(item.x)));

  const markerPoints = [];
  drawn.forEach(({ item, name }) => {
    (item.markers || []).forEach((marker, index) => {
      const mx = Number(marker.x);
      const my = Number.isFinite(Number(marker.y)) ? Number(marker.y) : yOnCurve(item.points, mx);
      if (!Number.isFinite(mx) || !Number.isFinite(my)) return;
      markerPoints.push({
        key: `${item.id || name}-${index}`, series: item.id || name, kind: marker.kind, x: mx, y: my,
      });
    });
  });

  // Hovering the plot shows every curve's value at that level, like a shadcn
  // line-chart tooltip. Near a current/recommended marker the crosshair snaps
  // onto it, so the exact operating points are easy to read off.
  const SNAP_PX = 14;
  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const zoom = cssZoom();
    const px = (e.clientX - rect.left) / zoom - padL;
    const py = (e.clientY - rect.top) / zoom - padT;
    if (px < 0 || px > plotW) { setTip(null); return; }
    let snap = null;
    let best = Infinity;
    markerPoints.forEach((m) => {
      const dx = Math.abs(xScale(m.x) - px);
      if (dx > SNAP_PX) return;
      const dist = Math.hypot(dx, yScale(m.y) - py);
      if (dist < best) { best = dist; snap = m; }
    });
    const x = snap ? snap.x : xAx.lo + (px / plotW) * (xAx.hi - xAx.lo);
    const items = drawn.map(({ item, name, color }) => {
      const id = item.id || name;
      if (snap && snap.series === id) {
        const shape = snap.kind === 'recommended' ? 'diamond' : snap.kind === 'current' ? 'dot' : 'line';
        return { id, name, color, y: snap.y, shape };
      }
      const pts = finitePoints(item.points);
      if (!pts.length || x < pts[0].x || x > pts[pts.length - 1].x) return null;
      const y = yOnCurve(item.points, x);
      return y == null ? null : { id, name, color, y, shape: 'line' };
    }).filter(Boolean);
    if (!items.length) { setTip(null); return; }
    setTip({
      cx: xScale(x),
      cy: snap ? yScale(snap.y) : Math.min(...items.map((it) => yScale(it.y))),
      label: snap ? `${kindLabel(snap.kind)} · ${fmtUnit(x, xAxis?.unit)}` : fmtUnit(x, xAxis?.unit),
      snapKey: snap ? snap.key : null,
      items: items.map(({ name, color, y, shape }) => ({ name, color, shape, value: fmtUnit(y, yAxis?.unit) })),
      dots: items.filter((it) => !snap || it.id !== snap.series).map(({ color, y }) => ({ color, cy: yScale(y) })),
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {yCaption && (
        <div style={{ fontSize: 14, color: 'var(--muted-foreground)' }}>{yCaption}</div>
      )}
      <ChartContainer ref={containerRef}>
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="group"
          aria-label={[yCaption, xCaption].filter(Boolean).join(' versus ') || 'Response curves'}
          style={{ display: 'block', overflow: 'visible' }}
          onMouseMove={onMove}
          onMouseLeave={() => setTip(null)}
        >
          <g transform={`translate(${padL},${padT})`}>
            {yAx.ticks.map((value) => (
              <g key={`y-${value}`}>
                <line x1={0} y1={yScale(value)} x2={plotW} y2={yScale(value)} stroke="var(--border)" strokeWidth={1} />
                <text x={-10} y={yScale(value)} textAnchor="end" dominantBaseline="middle" fill="var(--muted-foreground)" fontSize={12.5}>
                  {fmtNum(value)}
                </text>
              </g>
            ))}
            {xAx.ticks.map((value, i) => (
              <text
                key={`x-${value}`}
                x={xScale(value)}
                y={plotH + 19}
                textAnchor={i === 0 ? 'start' : i === xAx.ticks.length - 1 ? 'end' : 'middle'}
                fill="var(--muted-foreground)"
                fontSize={12.5}
              >
                {fmtNum(value)}
              </text>
            ))}
            {vlines.map((item, index) => {
              const x = Number(item.x);
              if (x < xAx.lo || x > xAx.hi) return null;
              return (
                <g key={`vline-${index}`}>
                  <line
                    x1={xScale(x)} y1={0} x2={xScale(x)} y2={plotH}
                    stroke="var(--muted-foreground)" strokeWidth={1} strokeDasharray="3 3"
                  />
                  {item.label && (
                    <text x={xScale(x) + 4} y={12} fill="var(--muted-foreground)" fontSize={12}>{item.label}</text>
                  )}
                </g>
              );
            })}
            {drawn.map(({ item, name, color, segments }) => (
              <g key={`band-${item.id || name}`}>
                {segments.map((seg, index) => {
                  const band = bandPath(seg.points, xScale, yScale);
                  return band ? (
                    <path key={index} d={band} fill={color} fillOpacity={seg.extrapolated ? 0.06 : 0.12} stroke="none" />
                  ) : null;
                })}
              </g>
            ))}
            {drawn.map(({ item, name, color, segments }) => (
              <g key={item.id || name}>
                {segments.map((seg, index) => {
                  const line = linePath(seg.points, xScale, yScale);
                  return line ? (
                    <path
                      key={`${name}-${index}`}
                      d={line}
                      fill="none"
                      stroke={color}
                      strokeWidth={2.25}
                      strokeOpacity={seg.extrapolated ? 0.75 : 1}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      strokeDasharray={seg.extrapolated ? '5 4' : undefined}
                      data-extrapolated={seg.extrapolated ? 'true' : 'false'}
                    />
                  ) : null;
                })}
              </g>
            ))}
            {tip && (
              <g pointerEvents="none">
                <line x1={tip.cx} y1={0} x2={tip.cx} y2={plotH} stroke="var(--muted-foreground)" strokeOpacity={0.4} />
                {tip.dots.map((d, i) => (
                  <circle key={i} cx={tip.cx} cy={d.cy} r={3.5} fill={d.color} stroke="var(--card)" strokeWidth={1.5} />
                ))}
              </g>
            )}
            {drawn.map(({ item, name, color }) => (
              <g key={`markers-${item.id || name}`}>
                {(item.markers || []).map((marker, index) => {
                  const mx = Number(marker.x);
                  const my = Number.isFinite(Number(marker.y)) ? Number(marker.y) : yOnCurve(item.points, mx);
                  if (!Number.isFinite(mx) || !Number.isFinite(my)) return null;
                  const cx = xScale(mx);
                  const cy = yScale(my);
                  const label = kindLabel(marker.kind);
                  const snapped = tip?.snapKey === `${item.id || name}-${index}`;
                  return (
                    <g
                      key={`${name}-marker-${index}`}
                      role="img"
                      aria-label={`${name} ${label}`}
                      data-marker={marker.kind || 'marker'}
                      data-series={item.id || name}
                      data-snapped={snapped ? 'true' : undefined}
                    >
                      {snapped && <circle cx={cx} cy={cy} r={11} fill={color} fillOpacity={0.18} />}
                      <MarkerShape kind={marker.kind} cx={cx} cy={cy} color={color} />
                    </g>
                  );
                })}
              </g>
            ))}
            <rect x={0} y={0} width={plotW} height={plotH} fill="transparent" pointerEvents="none" />
          </g>
        </svg>
        {tip && (
          <ChartTooltipContent
            x={padL + tip.cx}
            y={padT + tip.cy - 16}
            containerWidth={width}
            label={tip.label}
            items={tip.items}
          />
        )}
      </ChartContainer>
      {xCaption && (
        <div style={{ fontSize: 14, color: 'var(--muted-foreground)', textAlign: 'center' }}>{xCaption}</div>
      )}
      <ChartLegend style={{ marginTop: 10 }}>
        {drawn.map(({ name, color }) => (
          <ChartLegendItem key={name} color={color} shape="line" strong>{name}</ChartLegendItem>
        ))}
        {markerKinds.has('current') && (
          <ChartLegendItem color="var(--foreground)" shape="dot">Current</ChartLegendItem>
        )}
        {markerKinds.has('recommended') && (
          <ChartLegendItem color="var(--foreground)" shape="diamond">Recommended</ChartLegendItem>
        )}
        {hasExtrapolation && (
          <ChartLegendItem color="var(--muted-foreground)" shape="dashed">Outside observed range</ChartLegendItem>
        )}
      </ChartLegend>
    </div>
  );
}

const MODE_LABELS = {
  min_cost: 'Lowest cost to reach the target',
  min_spend: 'Lowest cost to reach the target',
  max_outcome: 'Most outcome within the budget',
};

function horizonText(horizon) {
  if (!horizon) return '';
  if (typeof horizon !== 'object') return prettyToken(horizon);
  const count = Number(horizon.count);
  const unitName = String(horizon.unit || '').replace(/_/g, ' ');
  if (!unitName) return '';
  if (!Number.isFinite(count) || count === 1) return `per ${unitName}`;
  return `per ${Number.isInteger(count) ? count : fmtNum(count)} ${unitName}s`;
}

function outcomeParts(outcome) {
  if (outcome == null) return null;
  if (typeof outcome !== 'object') return { value: fmtNum(outcome), mean: Number(outcome), range: null };
  const range = outcome.lo == null || outcome.hi == null ? null : `${fmtNum(outcome.lo)}–${fmtNum(outcome.hi)}`;
  return { value: fmtNum(outcome.mean), mean: Number(outcome.mean), range };
}

const STATUS_BADGE = {
  refused: () => 'Not allocated',
  unreachable: (maximizing) => (maximizing ? 'No mix fits the budget' : 'Target out of reach'),
};

// KPI tiles for the optimisation result. Which tiles appear depends on the
// mode: a min-cost run reports its target and total, a max-outcome run its
// budget and the chance of beating the current mix.
function allocationStats(allocation, unit) {
  if (!allocation) return [];
  const stats = [];
  const maximizing = allocation.mode === 'max_outcome';
  const per = horizonText(allocation.horizon);
  if (!maximizing && allocation.target != null) {
    stats.push({ key: 'target', label: 'Target', value: fmtNum(allocation.target), hint: per });
  }
  if (maximizing && allocation.budget != null) {
    stats.push({ key: 'budget', label: 'Budget', value: fmtUnit(allocation.budget, unit), hint: per });
  }
  const total = allocation.total_cost ?? allocation.total_spend;
  if (!maximizing && total != null) {
    stats.push({ key: 'total', label: 'Total cost', value: fmtUnit(total, unit), hint: per });
  }
  const current = outcomeParts(allocation.current_outcome);
  const recommended = outcomeParts(allocation.recommended_outcome);
  if (current) stats.push({ key: 'current', label: 'Current mix', value: current.value, hint: current.range });
  if (recommended) {
    const lift = current && Number.isFinite(current.mean) && current.mean !== 0 && Number.isFinite(recommended.mean)
      ? ((recommended.mean - current.mean) / Math.abs(current.mean)) * 100
      : null;
    stats.push({
      key: 'recommended',
      label: 'Recommended mix',
      value: recommended.value,
      hint: recommended.range,
      badge: lift != null ? `${lift > 0 ? '+' : ''}${lift.toFixed(1)}%` : null,
      tone: lift != null ? toneOf(lift) : null,
    });
  }
  if (maximizing && allocation.p_improve != null) {
    stats.push({ key: 'p', label: 'Beats current mix', value: fmtShare(allocation.p_improve), hint: 'of posterior draws' });
  }
  if (!maximizing && allocation.p_hit != null) {
    stats.push({ key: 'p', label: 'Meets target', value: fmtShare(allocation.p_hit), hint: 'of posterior draws' });
  }
  return stats;
}

function allocationStatus(allocation) {
  if (!allocation || !STATUS_BADGE[allocation.status]) return null;
  return STATUS_BADGE[allocation.status](allocation.mode === 'max_outcome');
}

function rowDelta(row) {
  if (Number.isFinite(Number(row.delta))) return Number(row.delta);
  if (Number.isFinite(Number(row.recommended)) && Number.isFinite(Number(row.current))) {
    return Number(row.recommended) - Number(row.current);
  }
  return null;
}

const DeltaCell = ({ delta }) => {
  const DeltaIcon = delta == null || delta === 0 ? Minus : delta > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
      <DeltaIcon size={13} aria-hidden style={{ color: 'var(--muted-foreground)' }} />
      {fmtSigned(delta)}
    </span>
  );
};

function AllocationTable({ allocation, unit, colors }) {
  const rows = allocation?.rows || [];
  const stats = allocationStats(allocation, unit);
  if (!stats.length && !rows.length) return null;
  const per = horizonText(allocation?.horizon);
  const unitSuffix = unit || per ? ` (${[unit, per].filter(Boolean).join(' ')})` : '';

  return (
    <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 20 }}>
      {stats.length > 0 && (
        <div className="q-stats">
          {stats.map((stat) => (
            <div key={stat.key} className="q-stat" data-stat={stat.key}>
              <span className="q-stat__label">{stat.label}</span>
              <span className="q-stat__value">
                {stat.value}
                {stat.badge && <Badge variant={stat.tone || 'outline'} className="ui-badge--mono">{stat.badge}</Badge>}
              </span>
              {stat.hint && <span className="q-stat__hint">{stat.hint}</span>}
            </div>
          ))}
        </div>
      )}
      {rows.length > 0 && (
        <div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead scope="col" style={{ paddingLeft: 0 }}>Treatment</TableHead>
                <TableHead scope="col" className="ui-table__right" style={{ whiteSpace: 'normal', lineHeight: 1.3 }}>{`Current${unitSuffix}`}</TableHead>
                <TableHead scope="col" className="ui-table__right" style={{ whiteSpace: 'normal', lineHeight: 1.3 }}>{`Recommended${unitSuffix}`}</TableHead>
                <TableHead scope="col" className="ui-table__right">Δ</TableHead>
                <TableHead scope="col" className="ui-table__right" style={{ paddingRight: 0 }}>Share</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, index) => {
                const name = seriesName(row, index);
                const color = colors[row.treatment] || colors[row.label] || SERIES_COLORS[index % SERIES_COLORS.length];
                const share = Number(row.share);
                return (
                  <TableRow key={row.treatment || name}>
                    <TableCell style={{ paddingLeft: 0 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
                        {name}
                      </span>
                    </TableCell>
                    <TableCell className="ui-table__right q-num q-muted">{fmtNum(row.current)}</TableCell>
                    <TableCell className="ui-table__right q-num" style={{ fontWeight: 500 }}>{fmtNum(row.recommended)}</TableCell>
                    <TableCell className="ui-table__right q-num"><DeltaCell delta={rowDelta(row)} /></TableCell>
                    <TableCell className="ui-table__right q-num" style={{ paddingRight: 0 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
                        {Number.isFinite(share) && (
                          <Progress value={share * 100} style={{ width: 44, height: 4 }} aria-label={`${name} share`} />
                        )}
                        <span style={{ minWidth: 42 }}>{fmtShare(row.share)}</span>
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

const EffectCurvesBlock = ({ blockData }) => {
  const data = blockData || {};
  const series = Array.isArray(data.series) ? data.series : [];
  const colors = {};
  series.forEach((item, index) => {
    const color = SERIES_COLORS[index % SERIES_COLORS.length];
    if (item.id) colors[item.id] = color;
    if (item.label) colors[item.label] = color;
  });
  const summary = typeof data.summary === 'string' ? data.summary.trim() : '';

  const allocation = data.allocation;
  const status = allocationStatus(allocation);
  const modeLabel = allocation?.mode ? (MODE_LABELS[allocation.mode] || prettyToken(allocation.mode)) : null;

  return (
    <Block
      icon="chart-spline"
      title="Response curves"
      description={modeLabel}
      badge={status ? <Badge variant="destructive">{status}</Badge> : null}
      footer={summary || null}
    >
      <EffectCurvesChart
        xAxis={data.x}
        yAxis={data.y}
        series={series}
        annotations={data.annotations}
      />
      <AllocationTable allocation={allocation} unit={data.x?.unit} colors={colors} />
    </Block>
  );
};

export default EffectCurvesBlock;
